import React from 'react'

function PrivacyPolicy() {
  return (
    <div id="privacy" className="basic-1">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <h1 className="blue text-center">Privacy Policy</h1>
            <p className="text-center"><em>Last updated: June 15, 2025</em></p>
            
            <div className="text-container">
              <h3>1. Introduction</h3>
              <p>
                FalconEye AI Solutions ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you use our AI solutions, autonomous agents, and related services.
              </p>

              <h3>2. Information We Collect</h3>
              
              <h4>2.1 Information You Provide</h4>
              <ul>
                <li><strong>Account Information:</strong> Name, email address, company details, job title</li>
                <li><strong>Business Data:</strong> Data you upload or connect through our MCP integrations</li>
                <li><strong>Communication Data:</strong> Messages, support requests, and feedback</li>
                <li><strong>Payment Information:</strong> Billing details processed through secure payment providers</li>
              </ul>

              <h4>2.2 Information We Collect Automatically</h4>
              <ul>
                <li><strong>Usage Data:</strong> How you interact with our AI systems and services</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system</li>
                <li><strong>Performance Data:</strong> AI model performance metrics and system analytics</li>
                <li><strong>Log Data:</strong> Server logs, error reports, and security events</li>
              </ul>

              <h4>2.3 Data from Third-Party Integrations</h4>
              <p>
                Through our Model Context Protocol (MCP) integrations, we may access data from your connected systems such as:
              </p>
              <ul>
                <li>Google Drive, Slack, GitHub repositories</li>
                <li>Business databases and CRM systems</li>
                <li>Enterprise applications and productivity tools</li>
                <li>Blockchain networks and smart contracts</li>
              </ul>

              <h3>3. How We Use Your Information</h3>
              
              <h4>3.1 Service Provision</h4>
              <ul>
                <li>Deliver AI-powered business intelligence and analytics</li>
                <li>Deploy and manage autonomous AI agents</li>
                <li>Facilitate MCP integrations with your data sources</li>
                <li>Provide blockchain-secured operations and audit trails</li>
              </ul>

              <h4>3.2 Service Improvement</h4>
              <ul>
                <li>Train and improve our AI models (using aggregated, anonymized data)</li>
                <li>Enhance system performance and reliability</li>
                <li>Develop new features and capabilities</li>
                <li>Conduct research and development</li>
              </ul>

              <h4>3.3 Communication and Support</h4>
              <ul>
                <li>Provide customer support and technical assistance</li>
                <li>Send service updates and security notifications</li>
                <li>Share relevant product information and best practices</li>
                <li>Respond to inquiries and feedback</li>
              </ul>

              <h3>4. Data Security and Protection</h3>
              
              <h4>4.1 Security Measures</h4>
              <p>We implement comprehensive security measures including:</p>
              <ul>
                <li><strong>Encryption:</strong> AES-256 encryption for data at rest and TLS 1.3 for data in transit</li>
                <li><strong>Access Controls:</strong> Multi-factor authentication and role-based access</li>
                <li><strong>Network Security:</strong> Firewalls, intrusion detection, and DDoS protection</li>
                <li><strong>Blockchain Security:</strong> Immutable audit trails and smart contract security</li>
                <li><strong>Regular Audits:</strong> Third-party security assessments and penetration testing</li>
              </ul>

              <h4>4.2 Data Isolation</h4>
              <p>
                Your business data is isolated and protected through:
              </p>
              <ul>
                <li>Tenant-specific data segregation</li>
                <li>Dedicated processing environments for sensitive data</li>
                <li>Secure API endpoints with authentication</li>
                <li>Zero-trust architecture principles</li>
              </ul>

              <h3>5. Data Sharing and Disclosure</h3>
              
              <h4>5.1 We Do Not Sell Your Data</h4>
              <p>
                We do not sell, rent, or trade your personal information or business data to third parties for marketing purposes.
              </p>

              <h4>5.2 Limited Sharing</h4>
              <p>We may share your information only in the following circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> Trusted partners who assist in service delivery (under strict confidentiality agreements)</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales (with notice to users)</li>
                <li><strong>Consent:</strong> When you explicitly authorize sharing</li>
              </ul>

              <h3>6. AI and Machine Learning</h3>
              
              <h4>6.1 Model Training</h4>
              <p>
                We may use aggregated, anonymized data to improve our AI models. This process:
              </p>
              <ul>
                <li>Removes all personally identifiable information</li>
                <li>Aggregates data across multiple customers</li>
                <li>Focuses on patterns and trends, not individual data points</li>
                <li>Enhances model accuracy and performance for all users</li>
              </ul>

              <h4>6.2 Autonomous Agents</h4>
              <p>
                Our AI agents process your data to perform assigned tasks. All agent activities are:
              </p>
              <ul>
                <li>Logged and auditable</li>
                <li>Subject to your defined permissions and constraints</li>
                <li>Monitored for compliance and security</li>
                <li>Reversible and controllable by authorized users</li>
              </ul>

              <h3>7. Your Rights and Choices</h3>
              
              <h4>7.1 Data Access and Control</h4>
              <p>You have the right to:</p>
              <ul>
                <li><strong>Access:</strong> Request copies of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Export your data in standard formats</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
              </ul>

              <h4>7.2 Communication Preferences</h4>
              <p>You can:</p>
              <ul>
                <li>Opt out of marketing communications</li>
                <li>Choose notification preferences</li>
                <li>Update contact information</li>
                <li>Manage subscription settings</li>
              </ul>

              <h3>8. Data Retention</h3>
              <p>
                We retain your data only as long as necessary to provide services and comply with legal obligations:
              </p>
              <ul>
                <li><strong>Active Accounts:</strong> Data retained while account is active</li>
                <li><strong>Inactive Accounts:</strong> Data deleted after 12 months of inactivity (with notice)</li>
                <li><strong>Legal Requirements:</strong> Some data may be retained longer for compliance</li>
                <li><strong>Anonymized Data:</strong> May be retained indefinitely for research and improvement</li>
              </ul>

              <h3>9. International Data Transfers</h3>
              <p>
                If you are located outside our primary jurisdiction, your data may be transferred to and processed in countries 
                with different privacy laws. We ensure adequate protection through:
              </p>
              <ul>
                <li>Standard contractual clauses</li>
                <li>Adequacy decisions</li>
                <li>Certification schemes</li>
                <li>Binding corporate rules</li>
              </ul>

              <h3>10. Compliance</h3>
              <p>
                We comply with applicable privacy laws including:
              </p>
              <ul>
                <li><strong>GDPR:</strong> European General Data Protection Regulation</li>
                <li><strong>CCPA:</strong> California Consumer Privacy Act</li>
                <li><strong>PIPEDA:</strong> Personal Information Protection and Electronic Documents Act (Canada)</li>
                <li><strong>Industry Standards:</strong> SOC 2, ISO 27001, and other relevant frameworks</li>
              </ul>

              <h3>11. Children's Privacy</h3>
              <p>
                Our services are not intended for children under 13 years of age. We do not knowingly collect personal information 
                from children under 13. If we become aware of such collection, we will delete the information immediately.
              </p>

              <h3>12. Changes to This Policy</h3>
              <p>
                We may update this Privacy Policy periodically. We will notify you of material changes via:
              </p>
              <ul>
                <li>Email notification to registered users</li>
                <li>Prominent notice on our website</li>
                <li>In-app notifications</li>
                <li>Updated "Last modified" date</li>
              </ul>

              <h3>13. Contact Us</h3>
              <p>
                For privacy-related questions or requests, contact us at:
              </p>
              <ul>
                <li><strong>Email:</strong> privacy@falconeye.io</li>
                <li><strong>Data Protection Officer:</strong> dpo@falconeye.io</li>
                <li><strong>Address:</strong> FalconEye AI Solutions Privacy Office</li>
                <li><strong>Support Portal:</strong> Available through your account dashboard</li>
              </ul>

              <div className="text-center" style={{marginTop: '40px'}}>
                <a className="btn-solid-reg" href="#contact">Contact Privacy Team</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy

